import { useCallback, useRef } from "react"
import { cancelIdle, scheduleIdle } from "lib/schedule-idle-callback"

/**
 * Async variant of usePreparedSavePayload backed by AuthoringScheduleWorkerClient.
 *
 * @param {{ workerClient: import("./authoring-schedule-worker-client").AuthoringScheduleWorkerClient | null, getPrepParams: () => object | null }} params
 * @returns {{ markDirty: () => void, schedulePrep: () => void, consumePayload: () => Promise<any | null>, releaseLock: () => void }}
 */
export function useAsyncPreparedSavePayload({ workerClient, getPrepParams }) {
    const getPrepParamsRef = useRef(getPrepParams)
    getPrepParamsRef.current = getPrepParams

    const dirtyRef = useRef(true)
    const handleRef = useRef(null)
    const lockedRef = useRef(false)

    const markDirty = useCallback(doMarkDirty, [workerClient])
    const schedulePrep = useCallback(doSchedulePrep, [workerClient])
    const consumePayload = useCallback(doConsumePayload, [workerClient])
    const releaseLock = useCallback(doReleaseLock, [workerClient])

    return { markDirty, schedulePrep, consumePayload, releaseLock }

    function doMarkDirty() {
        dirtyRef.current = true
        workerClient?.markSavePrepDirty()
        if (handleRef.current != null) {
            cancelIdle(handleRef.current)
            handleRef.current = null
        }
    }

    function doSchedulePrep() {
        if (lockedRef.current || !workerClient) return
        if (handleRef.current != null) cancelIdle(handleRef.current)
        handleRef.current = scheduleIdle(runIdle)
    }

    function runIdle() {
        handleRef.current = null
        if (lockedRef.current || !workerClient) return
        if (!dirtyRef.current) return

        const params = getPrepParamsRef.current()
        if (!params) return

        workerClient.scheduleSavePrep(params)
        dirtyRef.current = false
    }

    async function doConsumePayload() {
        if (handleRef.current != null) {
            cancelIdle(handleRef.current)
            handleRef.current = null
        }

        if (dirtyRef.current) {
            flushPendingPrep()
        }

        lockedRef.current = true

        if (!workerClient) {
            return null
        }

        return workerClient.consumeSavePrep()
    }

    function flushPendingPrep() {
        if (!workerClient || !dirtyRef.current) return

        const params = getPrepParamsRef.current()
        if (!params) return

        workerClient.scheduleSavePrep(params)
        dirtyRef.current = false
    }

    function doReleaseLock() {
        lockedRef.current = false
        workerClient?.releaseSavePrepLock()
    }
}
